import { useState, useEffect } from 'react';
import { Card, Button, Input, NumberInput, Select } from '@atoms/index';
import { cn } from '@/utils/cn';
import type { Promotion, CreatePromotionDto } from '@services/promotionService';
import { PromotionType } from '@services/promotionService';

interface PromotionFormModalProps {
  isOpen: boolean;
  promotion?: Promotion | null;
  onSave: (dto: CreatePromotionDto) => Promise<void>;
  onClose: () => void;
}

export function PromotionFormModal({
  isOpen,
  promotion,
  onSave,
  onClose,
}: PromotionFormModalProps) {
  const [formData, setFormData] = useState<CreatePromotionDto>({
    name: '',
    description: '',
    type: 'DISCOUNT_PERCENTAGE',
    discountValue: undefined,
    minimumOrderAmount: undefined,
    startDate: '',
    endDate: '',
    isActive: true,
    applicableProducts: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (promotion) {
      setFormData({
        name: promotion.name,
        description: promotion.description || '',
        type: promotion.type,
        discountValue: promotion.discountValue || undefined,
        minimumOrderAmount: promotion.minimumOrderAmount || undefined,
        startDate: new Date(promotion.startDate).toISOString().slice(0, 16),
        endDate: new Date(promotion.endDate).toISOString().slice(0, 16),
        isActive: promotion.isActive,
        applicableProducts: promotion.applicableProducts || null,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'DISCOUNT_PERCENTAGE',
        discountValue: undefined,
        minimumOrderAmount: undefined,
        startDate: '',
        endDate: '',
        isActive: true,
        applicableProducts: null,
      });
    }
  }, [promotion, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      return;
    }

    if (formData.type !== 'FREE_DELIVERY' && !formData.discountValue) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
      });
      onClose();
    } catch (error) {
      console.error('Failed to save promotion:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Modal kapat"
      />
      <Card className="relative z-[10000] w-full max-w-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            {promotion ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kampanya Adı *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Örn: Yeni Yıl İndirimi"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Kampanya açıklaması..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kampanya Tipi *
              </label>
              <Select
                value={formData.type}
                onChange={(value) =>
                  setFormData({ ...formData, type: value as PromotionType })
                }
                options={[
                  { value: 'DISCOUNT_PERCENTAGE', label: 'Yüzde İndirim' },
                  { value: 'DISCOUNT_AMOUNT', label: 'Tutar İndirimi' },
                  { value: 'FREE_DELIVERY', label: 'Ücretsiz Teslimat' },
                ]}
              />
            </div>

            {formData.type !== 'FREE_DELIVERY' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İndirim Değeri *
                  {formData.type === 'DISCOUNT_PERCENTAGE' && ' (%)'}
                  {formData.type === 'DISCOUNT_AMOUNT' && ' (₺)'}
                </label>
                <NumberInput
                  value={formData.discountValue != null ? String(formData.discountValue) : ''}
                  onChange={(s) =>
                    setFormData({
                      ...formData,
                      discountValue: s === '' ? undefined : (() => {
                        const n = parseFloat(s);
                        if (formData.type === 'DISCOUNT_PERCENTAGE') return Math.min(100, Math.max(0, isNaN(n) ? 0 : n));
                        return isNaN(n) ? undefined : n;
                      })(),
                    })
                  }
                  placeholder={formData.type === 'DISCOUNT_PERCENTAGE' ? '20' : '50'}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Sipariş Tutarı (₺)
              </label>
              <NumberInput
                value={formData.minimumOrderAmount != null ? String(formData.minimumOrderAmount) : ''}
                onChange={(s) =>
                  setFormData({
                    ...formData,
                    minimumOrderAmount: s === '' ? undefined : (() => { const n = parseFloat(s); return isNaN(n) ? undefined : n; })(),
                  })
                }
                placeholder="100"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlangıç Tarihi *
                </label>
                <Input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bitiş Tarihi *
                </label>
                <Input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="text-sm font-medium text-gray-700">Aktif</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 mt-6 justify-end">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
              İptal
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !formData.name ||
                !formData.startDate ||
                !formData.endDate ||
                (formData.type !== 'FREE_DELIVERY' && !formData.discountValue)
              }
              isLoading={isSubmitting}
            >
              {promotion ? 'Güncelle' : 'Oluştur'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
